import React, { useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";

const geoUrl = "https://unpkg.com/world-atlas@2/countries-110m.json";

const ORANGE = {
  primary: '#ff6b35',
  dark:    '#e85a28',
  darker:  '#d14d1f',
  light:   '#ff8c61',
};

/* ─── Severity color for a marker based on threat counts ─────────── */
function markerColor(agent) {
  if (!agent.threats) return ORANGE.light;
  const t = agent.threats;
  if (t.critical > 0) return "#8b0000";
  if (t.high     > 0) return "#dc3545";
  if (t.medium   > 0) return ORANGE.dark;
  return ORANGE.light;
}

/* ─── Dot radius scaled by total threat count ───────────────────── */
function markerRadius(total, max) {
  if (!total || !max) return 4;
  const minR = 4, maxR = 14;
  return minR + ((total / max) * (maxR - minR));
}

/* ─── Tooltip ────────────────────────────────────────────────────── */
function Tooltip({ agent, x, y }) {
  if (!agent) return null;
  const t = agent.threats || {};
  return (
    <div style={{
      position: "absolute",
      left:     x + 12,
      top:      y - 10,
      zIndex:   100,
      background: "#1e2433",
      border:   "1px solid #374151",
      borderRadius: 10,
      boxShadow: "0 4px 20px rgba(0,0,0,.4)",
      padding:  "12px 14px",
      minWidth: 200,
      pointerEvents: "none",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: markerColor(agent), flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: "#f3f4f6" }}>{agent.name}</span>
      </div>

      {/* Location */}
      <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
          {agent.country_code && (
            <img src={`https://flagcdn.com/16x12/${agent.country_code.toLowerCase()}.png`}
              alt="" style={{ borderRadius: 2 }} onError={e => e.target.style.display = "none"} />
          )}
          <span>{[agent.city, agent.country].filter(Boolean).join(", ") || "—"}</span>
        </div>
        {agent.isp && <div style={{ opacity: .75 }}>{agent.isp}</div>}
        {agent.public_ip && <code style={{ fontSize: 10, color: ORANGE.primary }}>{agent.public_ip}</code>}
      </div>

      {/* Threat counts */}
      {t.total > 0 && (
        <div style={{ borderTop: "1px solid #374151", paddingTop: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#6b7280", marginBottom: 6 }}>
            Threats
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 12px" }}>
            {[
              { label: "Total",    value: t.total,    color: ORANGE.primary },
              { label: "Critical", value: t.critical, color: "#8b0000"      },
              { label: "High",     value: t.high,     color: "#dc3545"      },
              { label: "Medium",   value: t.medium,   color: "#d97706"      },
            ].map(s => (
              <div key={s.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                <span style={{ color: "#9ca3af" }}>{s.label}</span>
                <strong style={{ color: s.color }}>{s.value?.toLocaleString() || 0}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status */}
      <div style={{ marginTop: 8, borderTop: "1px solid #374151", paddingTop: 6 }}>
        {(() => {
          const s = (agent.status || "").toLowerCase();
          const online  = s.includes("active") || s.includes("online");
          const offline = s.includes("disconnect") || s.includes("offline");
          const color   = online ? "#16a34a" : offline ? "#dc3545" : "#d97706";
          const label   = online ? "Online" : offline ? "Offline" : "Unknown";
          return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: `${color}15`, color, border: `1px solid ${color}40` }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: color }} />
              {label}
            </span>
          );
        })()}
        {agent.proxy && (
          <span style={{ marginLeft: 6, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "rgba(220,53,69,.1)", color: "#dc3545", border: "1px solid rgba(220,53,69,.3)" }}>
            VPN/Proxy
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── Main GlobalMap ─────────────────────────────────────────────── */
export default function GlobalMap({ agents = [], loading = false }) {
  const [tooltip,   setTooltip]   = useState(null);
  const [tooltipXY, setTooltipXY] = useState({ x: 0, y: 0 });

  // Collect highlighted country codes from agent data
  const highlightedCodes = new Set(agents.map(a => a.country_code).filter(Boolean));

  // Max threats for scaling dot size
  const maxThreats = Math.max(...agents.map(a => a.threats?.total || 0), 1);

  // Group agents by exact lat/lon to avoid perfect overlap
  const dedupedAgents = agents.reduce((acc, agent) => {
    if (!agent.lat || !agent.lon) return acc;
    const key = `${agent.lat.toFixed(2)},${agent.lon.toFixed(2)}`;
    if (!acc[key]) acc[key] = { ...agent, stacked: [agent] };
    else {
      acc[key].stacked.push(agent);
      acc[key].threats = {
        total:    (acc[key].threats?.total    || 0) + (agent.threats?.total    || 0),
        critical: (acc[key].threats?.critical || 0) + (agent.threats?.critical || 0),
        high:     (acc[key].threats?.high     || 0) + (agent.threats?.high     || 0),
        medium:   (acc[key].threats?.medium   || 0) + (agent.threats?.medium   || 0),
      };
    }
    return acc;
  }, {});

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>

      {/* Legend */}
      <div style={{ position: "absolute", bottom: 12, left: 12, zIndex: 10, background: "var(--vz-secondary-bg, #fff)", border: "1px solid var(--vz-border-color)", borderRadius: 8, padding: "8px 12px", display: "flex", flexDirection: "column", gap: 5 }}>
        <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#6b7280", margin: "0 0 4px" }}>Severity</p>
        {[
          { color: "#8b0000", label: "Critical" },
          { color: "#dc3545", label: "High"     },
          { color: ORANGE.dark,  label: "Medium"   },
          { color: ORANGE.light, label: "Low"      },
        ].map(s => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: "#9ca3af" }}>{s.label}</span>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 4, paddingTop: 4, borderTop: "1px solid var(--vz-border-color)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ width: 6,  height: 6,  borderRadius: "50%", background: ORANGE.primary, flexShrink: 0 }} />
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: ORANGE.primary, flexShrink: 0 }} />
            <span style={{ width: 14, height: 14, borderRadius: "50%", background: ORANGE.primary, flexShrink: 0 }} />
          </div>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>= Threat volume</span>
        </div>
      </div>

      {/* Agent count badge */}
      <div style={{ position: "absolute", top: 12, right: 12, zIndex: 10, background: `${ORANGE.primary}25`, border: `1px solid ${ORANGE.primary}50`, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: ORANGE.primary }}>
        {agents.length} agents plotted
      </div>

      {/* Loading overlay */}
      {loading && (
        <div style={{ position: "absolute", inset: 0, zIndex: 20, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(var(--vz-secondary-bg-rgb, 255,255,255),.7)", borderRadius: 8 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 32, height: 32, border: `3px solid ${ORANGE.primary}30`, borderTop: `3px solid ${ORANGE.primary}`, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 8px" }} />
            <p style={{ fontSize: 12, color: "var(--vz-secondary-color)", margin: 0 }}>Loading geo data...</p>
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && <Tooltip agent={tooltip} x={tooltipXY.x} y={tooltipXY.y} />}

      {/* Map */}
      <ComposableMap
        width={1000}
        height={480}
        projectionConfig={{ scale: 155, center: [20, 15] }}
        style={{ width: "100%", height: "100%", background: "#0f1929" }}
      >
        <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map(geo => {
                // iso_a2 code from topojson properties
                const code = geo.properties?.["Alpha-2"] || geo.properties?.iso_a2 || "";
                const isActive = highlightedCodes.has(code.toUpperCase());
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: {
                        fill:        isActive ? `${ORANGE.primary}30` : "var(--vz-light, #e9ecef)",
                        stroke:      "var(--vz-secondary-bg, #fff)",
                        strokeWidth: 0.5,
                        outline:     "none",
                      },
                      hover: {
                        fill:        isActive ? `${ORANGE.primary}50` : "#d1d5db",
                        stroke:      "var(--vz-secondary-bg, #fff)",
                        strokeWidth: 0.5,
                        outline:     "none",
                        cursor:      "default",
                      },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Agent markers */}
          {Object.values(dedupedAgents).map((agent, i) => {
            const r     = markerRadius(agent.threats?.total, maxThreats);
            const color = markerColor(agent);
            const count = agent.stacked?.length || 1;
            return (
              <Marker
                key={i}
                coordinates={[agent.lon, agent.lat]}
                onMouseEnter={e => {
                  setTooltip(agent);
                  setTooltipXY({ x: e.clientX - e.currentTarget.closest("svg").getBoundingClientRect().left, y: e.clientY - e.currentTarget.closest("svg").getBoundingClientRect().top });
                }}
                onMouseLeave={() => setTooltip(null)}
                onMouseMove={e => {
                  setTooltipXY({ x: e.clientX - e.currentTarget.closest("svg").getBoundingClientRect().left, y: e.clientY - e.currentTarget.closest("svg").getBoundingClientRect().top });
                }}
              >
                {/* Pulse ring for agents with critical threats */}
                {agent.threats?.critical > 0 && (
                  <circle r={r + 4} fill="none" stroke="#8b0000" strokeWidth={1} opacity={0.4}>
                    <animate attributeName="r" from={r} to={r + 8} dur="2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from={0.6} to={0} dur="2s" repeatCount="indefinite" />
                  </circle>
                )}
                {/* Main dot */}
                <circle
                  r={r}
                  fill={color}
                  stroke="var(--vz-secondary-bg, #fff)"
                  strokeWidth={1.5}
                  style={{ cursor: "pointer", transition: "r .2s" }}
                />
                {/* Stack count if multiple agents at same location */}
                {count > 1 && (
                  <text
                    textAnchor="middle"
                    y={r * 0.4}
                    style={{ fontSize: Math.max(6, r * 0.7), fontWeight: 700, fill: "#fff", pointerEvents: "none" }}
                  >
                    {count}
                  </text>
                )}
              </Marker>
            );
          })}
      </ComposableMap>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ─── Kept for backward compatibility ───────────────────────────── */
export function ResponsiveGlobalMap({ agents = [] }) {
  return <GlobalMap agents={agents} />;
}

export function CustomSizeGlobalMap({ agents = [], mapWidth = 1200, mapHeight = 600 }) {
  return (
    <div style={{ width: "100%", height: mapHeight }}>
      <GlobalMap agents={agents} />
    </div>
  );
}